function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
/**
 * Factory function to create type-safe store objects
 */ function createStore(definition) {
    return _extends({}, definition, {
        _get_value (value) {
            if (value === null && definition.defaultValue !== undefined) {
                // If no value found but we have a default, return the default
                return definition.defaultValue;
            }
            if (value === null) {
                return null;
            }
            // If no validator, just return the value as-is
            if (!definition.validator) {
                return value;
            }
            try {
                return definition.validator.parse(value);
            } catch (error) {
                console.error(`Invalid schema for ${definition.key}:`, error);
                return null;
            }
        },
        get (provider) {
            const value = provider.get(definition.storageType, definition.key);
            return this._get_value(value);
        },
        _set_value (value) {
            // If no validator, just return the value as-is
            if (!definition.validator) {
                return value;
            }
            try {
                return definition.validator.parse(value);
            } catch (error) {
                throw new Error(`Failed to store value for ${definition.key}: ${error}`);
            }
        },
        set (provider, value) {
            const validatedValue = this._set_value(value);
            provider.set(definition.storageType, definition.key, validatedValue);
        },
        remove (provider) {
            provider.remove(definition.storageType, definition.key);
        },
        exists (provider) {
            return provider.has(definition.storageType, definition.key);
        }
    });
}

/**
 * Browser implementation of StorageProvider using localStorage and sessionStorage
 */ class BrowserStorageProvider {
    /**
   * Get a value from browser storage
   * @param type - Storage type (local, session, cookie)
   * @param key - Storage key
   * @returns The value or null if not found
   */ get(type, key) {
        if (typeof window === "undefined") {
            console.error(`${type}Storage is not available in this environment`);
            return null;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return null;
            const item = storage.getItem(key);
            if (item === null) {
                return null;
            }
            return JSON.parse(item);
        } catch (error) {
            console.error(`Failed to get item from ${type}Storage: ${error}`);
            return null;
        }
    }
    /**
   * Set a value in browser storage
   * @param type - Storage type (local, session, cookie)
   * @param key - Storage key
   * @param value - Value to store
   */ set(type, key, value) {
        if (typeof window === "undefined") {
            return;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return;
            const serializedValue = JSON.stringify(value);
            storage.setItem(key, serializedValue);
        } catch (error) {
            console.error(`Failed to set item in ${type}Storage: ${error}`);
        }
    }
    /**
   * Remove a value from browser storage
   * @param type - Storage type (local, session, cookie)
   * @param key - Storage key
   */ remove(type, key) {
        if (typeof window === "undefined") {
            return;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return;
            storage.removeItem(key);
        } catch (error) {
            console.error(`Failed to remove item from ${type}Storage: ${error}`);
        }
    }
    /**
   * Clear all values from browser storage
   * @param type - Storage type (local, session, cookie)
   */ clear(type) {
        if (typeof window === "undefined") {
            return;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return;
            storage.clear();
        } catch (error) {
            console.error(`Failed to clear ${type}Storage: ${error}`);
        }
    }
    /**
   * Check if a key exists in browser storage
   * @param type - Storage type (local, session, cookie)
   * @param key - Storage key
   * @returns True if the key exists
   */ has(type, key) {
        if (typeof window === "undefined") {
            return false;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return false;
            return storage.getItem(key) !== null;
        } catch (error) {
            console.error(`Failed to check ${type}Storage for an item: ${error}`);
            return false;
        }
    }
    /**
   * Check if this storage type can be used at all in the current environment
   * @param type - Storage type (local, session, cookie)
   * @returns False if the underlying storage doesn't exist, or exists but a
   *   write/read/remove throws (e.g. Safari private browsing, where
   *   `localStorage` is present but `setItem` throws due to a zero quota)
   */ isAvailable(type) {
        if (typeof window === "undefined") {
            return false;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return false;
            const probeKey = "__quotient_storage_probe__";
            const probeValue = `${Math.random()}`;
            // Preserve any pre-existing value at the probe key so a real caller
            // that happened to use it doesn't get clobbered by this check.
            const previousValue = storage.getItem(probeKey);
            storage.setItem(probeKey, probeValue);
            const isAvailable = storage.getItem(probeKey) === probeValue;
            if (previousValue === null) {
                storage.removeItem(probeKey);
            } else {
                storage.setItem(probeKey, previousValue);
            }
            return isAvailable;
        } catch (e) {
            return false;
        }
    }
    /**
   * Get the appropriate storage object based on type
   * @param type - Storage type (local, session, cookie)
   * @returns The storage object
   */ getStorageByType(type) {
        if (typeof window === "undefined") {
            return null;
        }
        switch(type){
            case "local":
                return window.localStorage;
            case "session":
                return window.sessionStorage;
            case "cookie":
                console.warn("Cookie storage not yet implemented in BrowserStorageProvider");
                return null;
            default:
                return null;
        }
    }
    constructor(){
        if (typeof window === "undefined") {
            throw new Error("BrowserStorageProvider is not available in this environment");
        }
    }
}

export { BrowserStorageProvider, createStore };
